import { ThemeEvents, DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Attaches a discount code to the cart automatically, so customers don't have to type it.
 *
 * The code is attached as soon as the cart has items. Shopify then evaluates the code's own
 * rules (e.g. "buy 3 from these collections, get 1 free") on every cart change, so the saving
 * shows up in the cart drawer the moment the cart qualifies and disappears when it no longer does.
 *
 * The code is added next to any code the customer already uses; Shopify applies whichever
 * combination saves the most. Once the customer removes the code via its pill, it's not
 * re-attached for the rest of the session.
 */

const config = document.getElementById('auto-discount-code-config');
const CODE = config?.dataset.code?.trim();
const SOURCE_ID = 'auto-discount-code';
const DISMISSED_KEY = `auto-discount-removed:${CODE}`;

/** @param {string} code */
const isOurCode = (code) => code.toLowerCase() === CODE?.toLowerCase();

/** @param {{ discount_codes?: { code: string }[] }} cart */
const cartCodes = (cart) => (cart.discount_codes || []).map((discount) => discount.code);

function isDismissed() {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === 'true';
  } catch (error) {
    return false;
  }
}

function setDismissed() {
  try {
    sessionStorage.setItem(DISMISSED_KEY, 'true');
  } catch (error) {}
}

let busy = false;
let rerun = false;

async function reconcile() {
  if (busy) {
    rerun = true;
    return;
  }
  busy = true;

  try {
    do {
      rerun = false;
      if (isDismissed()) return;

      const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();
      const codes = cartCodes(cart);

      if (cart.item_count === 0 || codes.some(isOurCode)) continue;

      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body: JSON.stringify({ discount: [...codes, CODE].join(',') }) }),
      });
      const data = await response.json();

      // Lets the cart drawer and cart page re-render with the discount.
      document.dispatchEvent(new DiscountUpdateEvent(data, SOURCE_ID));
    } while (rerun);
  } catch (error) {
    console.warn('[auto-discount-code]', error);
  } finally {
    busy = false;
  }
}

if (CODE) {
  document.addEventListener(ThemeEvents.cartUpdate, () => setTimeout(reconcile, 60));

  // The theme's discount form only resubmits codes that are currently applied, so entering
  // or removing another code can drop ours while the cart doesn't qualify yet. Put it back.
  document.addEventListener(ThemeEvents.discountUpdate, (event) => {
    if (event instanceof DiscountUpdateEvent && event.detail.sourceId === SOURCE_ID) return;
    setTimeout(reconcile, 60);
  });

  // Capture phase, so the flag is set before the theme's own remove handler runs.
  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const pill = event.target.closest('.cart-discount__pill');
      if (pill instanceof HTMLElement && event.target.closest('.cart-discount__pill-remove')) {
        if (isOurCode(pill.dataset.discountCode || '')) setDismissed();
      }
    },
    true
  );

  reconcile();
}
